import Header from '@/components/Header';
import DeliveryAddressInput from '@/components/front-office/DeliveryAddressInput';
import LargeContainer from '@/components/front-office/containers/LargeContainer';
import { routes } from '@/constants/routes';
import useDeliveryFees from '@/hooks/useDeliveryFees';
import { useOrdersContext } from '@/contexts/OrderContext';
import { useEffect, useState } from 'react';
import Button from '@/components/Button';
import Input from '@/components/Input';
import CityAutocomplete from '@/components/front-office/CityAutocomplete';
import { useNavigate } from 'react-router-dom';

export default function AddressAutocomplete() {
	const { cart, setCart, fetchInitialCategories } = useOrdersContext();
	const { fetchAddressDeliveryFees } = useDeliveryFees();
	const [showManualAddress, setShowManualAddress] = useState(false);
	const [manualAddress, setManualAddress] = useState({
		address: '',
		postal_code: '',
		city: '',
		region: '',
		country: 'France',
	});
	const [isVerifyingAddress, setIsVerifyingAddress] = useState(false);
	const navigate = useNavigate();

  const isCorsicaAddress = (address) => {
    if (!address) return false;
    const postalCode = String(address.postal_code || '').trim();
    const region = String(address.region || '').toLowerCase();
    const country = String(address.country || '').toLowerCase();
    const isFrance = !country || country === 'france' || country === 'fr';
    if (!isFrance) return false;
    return postalCode.startsWith('20') || region.includes('corse') || region.includes('corsica');
  };

	useEffect(() => {
		// Only call the API if delivery_address exists and delivery_fee_applied is not already set
		if (cart.delivery_address) {
			if (cart.categories.length === 0) {
				fetchInitialCategories();
			}

			setIsVerifyingAddress(true);
			fetchAddressDeliveryFees().finally(() => {
				setIsVerifyingAddress(false);
			});
		}
	}, [cart.delivery_address]);

	const handleManualAddressChange = (e) => {
		setManualAddress({
			...manualAddress,
			[e.target.name]: e.target.value,
		});
	};

	const handleCitySelect = (cityData) => {
		setManualAddress({
			...manualAddress,
			city: cityData.city,
			postal_code: cityData.postal_code,
			region: cityData.region,
			country: cityData.country,
		});
	};

	const handleSubmitManualAddress = async () => {
		// Concaténer l'adresse complète pour l'affichage
		const fullAddress = `${manualAddress.address}, ${manualAddress.postal_code} ${manualAddress.city}`;
		
		// Créer un objet adresse similaire à celui retourné par l'API d'autocomplétion
		const addressObject = {
			address: fullAddress,
			postal_code: manualAddress.postal_code,
			city: manualAddress.city,
			country: manualAddress.country,
			region: manualAddress.region,
		};

		// Mettre à jour le panier avec l'adresse saisie manuellement
		await setCart((prevCart) => ({
			...prevCart,
			delivery_address: addressObject,
		}));

		// Fermer le formulaire de saisie manuelle
		setShowManualAddress(false);
		
		// Naviguer vers la page suivante
		handleNextStepClick();
	};

	const handleNextStepClick = () => {
		// Debug pour vérifier l'état du cart
		console.log('AddressAutocomplete - cart.is_no_limit:', cart.is_no_limit);
		console.log('AddressAutocomplete - cart.is_no_limit_type:', cart.is_no_limit_type);
		console.log('AddressAutocomplete - cart.is_no_limit_selected_category:', cart.is_no_limit_selected_category);

		// Restaurant en mode no limit - liste de produits
		if (cart.is_no_limit && cart.is_no_limit_type === 'list') {
			navigate('/commande/liste-des-produits', { replace: true });
			return;
		}

		// Restaurant en mode no limit - catégorie spécifique
		if (cart.is_no_limit && cart.is_no_limit_type === 'category' && cart.is_no_limit_selected_category) {
			navigate(`/commande/${cart.is_no_limit_selected_category.name.toLowerCase().split(' ')
				.join('-')
				.replace(',', '-')}`, { replace: true });
			return;
		}

		// Restaurant en mode no limit mais configuration incomplète - fallback vers liste
		if (cart.is_no_limit && (!cart.is_no_limit_type || cart.is_no_limit_type === 'category' && !cart.is_no_limit_selected_category)) {
			console.warn('Restaurant en mode no limit mais configuration incomplète, redirection vers liste des produits');
			navigate('/commande/liste-des-produits', { replace: true });
			return;
		}

		// Restaurant normal (avec menus/formules)
		if (!cart.is_no_limit) {
			navigate('/commande/menu', { replace: true });
			return;
		}

		// Fallback - ne devrait jamais arriver
		console.error('Cas non géré dans handleNextStepClick, redirection vers menu par défaut');
		navigate('/commande/menu', { replace: true });
	};

	return (
		<LargeContainer>
			<Header
				useLogo={false}
				heading="Renseignez votre adresse"
				paragraph={showManualAddress ? "Je connais l'adresse exacte" : "Vous ne trouvez pas votre adresse ?"}
				linkName="Cliquez ici"
				linkUrl="#"
				onLinkClick={() => setShowManualAddress(!showManualAddress)}
			/>

			{!showManualAddress ? (
				<DeliveryAddressInput
					classes={'w-full mx-auto border border-primary px-2 py-1 rounded-[10px] lg:w-3/5'}
					field={{
						id: 'delivery_address',
						name: 'delivery_address',
						type: 'text',
						labelFor: 'delivery_address',
						labelText: 'Adresse de livraison',
						placeholder: 'Adresse de livraison',
						isRequired: true,
					}}
				/>
			) : (
				<div className="w-full mx-auto lg:w-3/5 space-y-4">
					<p className="text-center text-sm font-medium text-gray-600 mb-2">Tous les champs sont obligatoires</p>
					<Input
						handleChange={handleManualAddressChange}
						value={manualAddress.address}
						labelText="Adresse exacte *"
						labelFor="address"
						id="address"
						name="address"
						type="text"
						isRequired={true}
						placeholder="Entrez l'adresse exacte"
						customClass="w-full px-3 py-2"
						divClass="w-full"
					/>
					<Input
						handleChange={handleManualAddressChange}
						value={manualAddress.postal_code}
						labelText="Code postal *"
						labelFor="postal_code"
						id="postal_code"
						name="postal_code"
						type="text"
						isRequired={true}
						placeholder="Entrez le code postal"
						customClass="w-full px-3 py-2"
						divClass="w-full"
					/>
					<CityAutocomplete
						onCitySelect={handleCitySelect}
						labelText="Ville *"
						labelFor="city"
						id="city"
						name="city"
						isRequired={true}
						placeholder="Entrez votre ville"
						customClass="w-full px-3 py-2"
						divClass="w-full"
					/>
										{isCorsicaAddress(manualAddress) && (
						<p className="text-center text-primary mt-2">Nous ne livrons pas encore en Corse</p>
					)}
					<div className="flex justify-center mt-4">
						<Button
							disabled={manualAddress.address && manualAddress.postal_code && manualAddress.city && !isCorsicaAddress(manualAddress)}
							handleClick={handleSubmitManualAddress}
							text="Valider"
						/>
					</div>
				</div>
			)}

			{isVerifyingAddress && (
				<p className="text-center text-primary">Vérification de l'adresse en cours...</p>
			)}

      {!isVerifyingAddress && cart.delivery_address && (
        isCorsicaAddress(cart.delivery_address) ? (
          <p className="text-center text-primary">Nous ne livrons pas encore en Corse</p>
        ) : (
          !cart.delivery_fee_applied?.id && (
            <p className="text-center text-primary">Nous ne livrons pas à cette adresse</p>
          )
        )
      )}
		</LargeContainer>
	);
}
